// Deep linky do externich navigacnich app.
//
// CIL: kliknuti ma SPUSTIT NAVIGACI z aktualni polohy, ne jen ukazat spendlik na mape.
// Driv to tak nefungovalo — Mapy.cz dostavaly `source=coor` (jen bod) a Google
// `dir/?api=1` (jen nahled trasy), takze ridic musel po otevreni jeste jednou
// odklepnout „Navigovat".
//
// Na nativni appce se proto nejdriv zkousi APLIKACNI SCHEMA, ktere navigaci spusti
// rovnou; web URL zustava jako fallback pro PWA v prohlizeci.

import { Capacitor } from "@capacitor/core";

export interface NavTarget {
  lat: number;
  lng: number;
  label?: string;
}

export type NavProvider = "mapy" | "waze" | "google";

/** Web URL — fallback pro PWA a pro pripad, ze aplikacni schema nikdo neobslouzi. */
export function navigationUrl(target: NavTarget, provider: NavProvider): string {
  const { lat, lng } = target;
  switch (provider) {
    case "mapy":
      // ⚠️ Mapy.cz nemaji verejne zdokumentovany link, ktery by rovnou spustil
      // navigaci — tohle otevre bod v appce a ridic klepne „Navigovat". Neni to
      // vymyslena URL, ktera by tise nefungovala; je to o jeden dotek vic.
      return `https://mapy.cz/zakladni?x=${lng}&y=${lat}&z=17&source=coor&id=${lng},${lat}`;
    case "waze":
      // `navigate=yes` navigaci spusti rovnou (dokumentovane).
      return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    case "google":
      // `dir_action=navigate` = spustit navigaci, ne jen ukazat trasu (Maps URLs API).
      // Cil je VZDY jen souradnice — nazev pobocky by Google prehodil na vyhledavani
      // a mohl trefit jinou provozovnu stejneho jmena.
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        + `&travelmode=driving&dir_action=navigate`;
  }
}

/** Aplikacni schema, ktere na telefonu spusti navigaci rovnou. `null` = jen web. */
export function navigationAppUrl(target: NavTarget, provider: NavProvider): string | null {
  const { lat, lng } = target;
  switch (provider) {
    case "google":
      // Dokumentovany Android intent — otevre Google Maps rovnou v navigaci.
      return `google.navigation:q=${lat},${lng}`;
    case "waze":
      return `waze://?ll=${lat},${lng}&navigate=yes`;
    case "mapy":
      return null;   // viz poznamka u web URL
  }
}

/** Navigace na ADRESU (text), ne na souradnice — TRANK sva mista souradnice nenese. */
export function navigationSearchUrl(query: string, provider: NavProvider): string {
  const q = encodeURIComponent(query);
  switch (provider) {
    case "mapy":
      return `https://mapy.cz/zakladni?q=${q}`;
    case "waze":
      // `navigate=yes` i u vyhledavani — driv chybelo, takze Waze jen ukazal misto.
      return `https://waze.com/ul?q=${q}&navigate=yes`;
    case "google":
      // `search/` jen ukazalo pin. `dir/` + `dir_action=navigate` rovnou naviguje.
      return `https://www.google.com/maps/dir/?api=1&destination=${q}`
        + `&travelmode=driving&dir_action=navigate`;
  }
}

function openUrl(url: string): void {
  if (Capacitor.isNativePlatform()) {
    window.open(url, "_system");
  } else {
    // PWA: otevrit v nove zalozce; po redirectu do nativni app zavrit blank tab
    const win = window.open(url, "_blank");
    if (win) setTimeout(() => { try { win.close(); } catch { /* cross-origin, ignore */ } }, 1500);
  }
}

/** Spusti navigaci na adresu zadanou textem (TRANK). */
export function openNavigationSearch(query: string, provider: NavProvider): void {
  openUrl(navigationSearchUrl(query, provider));
}

export function openNavigation(target: NavTarget, provider: NavProvider): void {
  const web = navigationUrl(target, provider);
  if (Capacitor.isNativePlatform()) {
    // Aplikacni schema ma prednost (spusti navigaci rovnou), web je zaloha.
    const app = navigationAppUrl(target, provider);
    if (app) {
      try {
        window.open(app, "_system");
        return;
      } catch {
        /* schema neobslouzeno (app neni nainstalovana) → spadni na web */
      }
    }
  }
  openUrl(web);
}
